import { Drawer, Tabs } from 'antd'
import FootprintHistory from 'components/kol/common/kol-cooperation-detail-drawer/footprint-history'
import { FunctionComponent, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import ContactInfo from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info'
import CooperationHistory from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history'
import Custom from '@/components/kol/common/kol-cooperation-detail-drawer/custom'
import Header from '@/components/kol/common/kol-cooperation-detail-drawer/header'
import Notes from '@/components/kol/common/kol-cooperation-detail-drawer/notes'
import Overview from '@/components/kol/common/kol-cooperation-detail-drawer/overview'
import Quotation from '@/components/kol/common/kol-cooperation-detail-drawer/quotation'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { createTourStepData } from '@/components/tour/utils'
import { useDispatch } from '@/hooks/use-dispatch'
import useExitPageDwellTime from '@/hooks/use-exit-page-dwell-time'
import { useSelector } from '@/hooks/use-selector'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  TabKey,
  setActiveTabKey,
  setOpen,
  tabKeySchema,
} from '@/store/kol-cooperation-detail-drawer'
import { AccessKolDrawerTabProperties, ampli } from '@/utils/ampli'

const TAB_KEY_MAPPING: Record<TabKey, AccessKolDrawerTabProperties['tab']> = {
  overview: 'Overview',
  contactInfo: 'Contact Information',
  cooperationHistory: 'Collaboration Records',
  quotation: 'Inquiry Records',
  notes: 'Notes',
  footprint: 'Activity Log',
  custom: 'Custom',
}

const KolCooperationDetailDrawer: FunctionComponent = () => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const {
    data: { kolId, at: from },
    open,
  } = useSelector((state) => state.kolCooperationDetailDrawer)
  const activeTabKey = useSelector(
    (state) => state.kolCooperationDetailDrawer.activeTabKey,
  )
  const { kolInfo, isFetching: isFetchingKolInfo } = useKolInfo(kolId)

  useExitPageDwellTime((second, isPageHideTrigger): void => {
    // 判斷 open === isPageHideTrigger 的情境是避免非預期的情境：分頁關閉時 drawer 沒開啟時不應處發 event
    if (kolId && open === isPageHideTrigger) {
      ampli.exitKolDrawer({
        kolId,
        kolName: kolInfo?.name,
        at: from,
        second,
      })
    }
  }, open)

  useEffect(() => {
    if (open && kolInfo?.name && !isFetchingKolInfo) {
      ampli.visitKolDrawer({
        kolId,
        kolName: kolInfo.name,
        at: from,
      })
    }
  }, [open, kolId, kolInfo?.name, from, isFetchingKolInfo])

  if (!kolId) {
    return null
  }

  const tabItems = tabKeySchema.options.map((key) => {
    switch (key) {
      case 'overview':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_overview',
          }),
          children: <Overview from={from} kolUuid={kolId} />,
        }
      case 'contactInfo':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_contact_info',
          }),
          children: <ContactInfo kolId={kolId} />,
        }
      case 'cooperationHistory':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_cooperation_history',
          }),
          children: <CooperationHistory kolInfo={kolInfo} />,
        }
      case 'quotation':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_quotation',
          }),
          children: <Quotation kolInfo={kolInfo} />,
        }
      case 'notes':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_notes',
          }),
          children: <Notes from={from} kolId={kolId} />,
        }
      case 'footprint':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_footprint',
          }),
          children:
            activeTabKey === 'footprint' ? (
              <FootprintHistory kolId={kolId} />
            ) : null,
        }
      case 'custom':
        return {
          key,
          label: formatMessage({
            id: 'kol_cooperation_detail_drawer:tabs_custom',
          }),
          children: <Custom from={from} kolId={kolId} />,
        }
    }
  })

  return (
    <StyledDrawer
      $backgroundColor={
        activeTabKey === 'overview' ? theme.colors.background.grey : undefined
      }
      {...createTourStepData('directory', 7)}
      {...createTourStepData('kol-collections', 6)}
      getContainer={false}
      key={kolId}
      open={open}
      rootStyle={{ position: 'fixed' }}
      title={<Header from={from} kolId={kolId} />}
      width={600}
      onClose={(): void => {
        dispatch(setOpen(false))
      }}
    >
      <Tabs
        activeKey={activeTabKey}
        items={tabItems}
        onChange={(key: TabKey) => {
          ampli.accessKolDrawerTab({
            kolId,
            kolName: kolInfo?.name,
            tab: TAB_KEY_MAPPING[key],
          })
          dispatch(setActiveTabKey(tabKeySchema.parse(key)))
        }}
      />
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)<{ $backgroundColor?: string }>`
  overflow: unset !important;

  .ant-drawer-header {
    padding: 0;
    margin: 0 0 16px;
    border: none;

    .ant-drawer-header-title .ant-drawer-title {
      overflow: hidden;
    }

    .ant-drawer-close {
      display: none;
    }
  }

  .ant-drawer-body {
    overflow: unset;
    padding: 0;

    .ant-tabs {
      height: 100%;

      .ant-tabs-nav {
        margin: 0;
        border-bottom: 1px solid
          ${({ theme }): string => theme.colors.border.border};
      }

      .ant-tabs-nav-wrap {
        padding: 0 24px;

        .ant-tabs-tab {
          padding: 0 16px;
          .ant-tabs-tab-btn {
            line-height: 40px;
          }
        }
      }

      .ant-tabs-content-holder {
        overflow-y: auto;
        background: ${({ $backgroundColor, theme }): string =>
          $backgroundColor ?? theme.colors.background.white};

        .ant-tabs-content {
          height: 100%;

          .ant-tabs-tabpane {
            height: 100%;
          }
        }
      }
    }
  }
`

export default KolCooperationDetailDrawer
